import { Suspense, lazy, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { ThemeProvider } from './components/theme-provider'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { useAuthStore } from './store/AuthStore'
import { SidebarProvider, SidebarTrigger } from './components/ui/sidebar'
import { AppSidebar } from './components/app-sidebar'
import { Skeleton } from './components/ui/skeleton'
import { canAccessRoute, getDefaultRoute } from './lib/rbac'

// Lazy-loaded pages for route-level code splitting
const HomePage = lazy(() => import('./pages/HomePage'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const CounselorProfilePage = lazy(() => import('./pages/CounselorProfilePage'))
const CounselorProfileReadOnlyPage = lazy(() => import('./pages/CounselorProfileReadOnlyPage'))
const BroadcastPage = lazy(() => import('./pages/BroadcastPage'))
const WithdrawalsPage = lazy(() => import('./pages/WithdrawalsPage'))
const WithdrawalDetailPage = lazy(() => import('./pages/WithdrawalDetailPage'))
const ProcessedWithdrawalPage = lazy(() => import('./pages/ProcessedWithdrawalPage'))
const AllEventsPage = lazy(() => import('./pages/AllEventsPage'))
const AddEventPage = lazy(() => import('./pages/AddEventPage'))
const EditEventPage = lazy(() => import('./pages/EditEventPage'))
const EventDetailPage = lazy(() => import('./pages/EventDetailPage'))
const UsersPage = lazy(() => import('./pages/UsersPage'))
const UserDetailPage = lazy(() => import('./pages/UserDetailPage'))
const CourseManagementPage = lazy(() => import('./pages/CourseManagementPage'))
const CollegeFeesPage = lazy(() => import('./pages/CollegeFeesPage'))
const PCSATRegistrationsPage = lazy(() => import('./pages/PCSATRegistrationsPage'))
const TestSeriesPage = lazy(() => import('./pages/TestSeriesPage'))
const GrandMockTestPage = lazy(() => import('./pages/GrandMockTestPage'))
const BlogsPage = lazy(() => import('./pages/BlogsPage.tsx'))
const EnquiriesPage = lazy(() => import('./pages/EnquiriesPage'))
const ProBuddiesPage = lazy(() => import('./pages/ProBuddiesPage'))
const ProBuddyProfilePage = lazy(() => import('./pages/ProBuddyProfilePage'))
const ReferrersPage = lazy(() => import('./pages/ReferrersPage'))
const LeadsPage = lazy(() => import('./pages/LeadsPage'))
const MyLeadsPage = lazy(() => import('./pages/MyLeadsPage'))
const AbroadLeadsPage = lazy(() => import('./pages/AbroadLeadsPage'))
const NeetPredictorPage = lazy(() => import('./pages/NeetPredictorPage'))
const AdminAppointmentsPage = lazy(() => import('./pages/AdminAppointmentsPage'))
const OptionFormRegistrationsPage = lazy(() => import('./pages/OptionFormRegistrationsPage'))
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'))
const CreateAdminPage = lazy(() => import('./pages/CreateAdminPage'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))
const ErrorPage = lazy(() => import('./pages/ErrorPage'))

function RouteLoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="space-y-3 w-full max-w-2xl p-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-4 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    </div>
  )
}

// Protected Route Component with Role-Based Access Control
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isInitialized } = useAuthStore()
  const location = useLocation()

  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="space-y-4 w-full max-w-md mx-auto p-6">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <div className="grid grid-cols-3 gap-4 mt-8">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        </div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Check if user has access to this route based on their role
  const userRole = user.role || 'superAdmin'
  const hasRouteAccess = canAccessRoute(userRole, location.pathname)

  if (!hasRouteAccess) {
    // Redirect to their default route if they don't have access
    const defaultRoute = getDefaultRoute(userRole)
    return <Navigate to={defaultRoute} replace />
  }

  return <>{children}</>
}

// Layout Component
function MainLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const hideSidebar = location.pathname === '/login'

  if (hideSidebar) {
    return <main>{children}</main>
  }

  return (
    <SidebarProvider style={{ '--sidebar-width': '18rem' } as React.CSSProperties}>
      <AppSidebar />
      <main className="pt-2 flex-1 min-w-0">
        <SidebarTrigger />
        {children}
      </main>
    </SidebarProvider>
  )
}

// Auth Initializer Component
function AuthInitializer({ children }: { children: React.ReactNode }) {
  const { initializeAuth } = useAuthStore()

  useEffect(() => {
    initializeAuth()
  }, [initializeAuth])

  return <>{children}</>
}

function App() {
  return (
    <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
      <div className="antialiased bg-background text-foreground min-h-screen">
        <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        toastStyle={{ zIndex: 99999 }}
      />
      <BrowserRouter>
        <AuthInitializer>
          <MainLayout>
            <Suspense fallback={<RouteLoadingFallback />}>
              <Routes>
                {/* Public Route */}
                <Route path="/login" element={<LoginPage />} />

              {/* Protected Routes */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <HomePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/counselor-profile"
                element={
                  <ProtectedRoute>
                    <CounselorProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/counselor-profile-readonly"
                element={
                  <ProtectedRoute>
                    <CounselorProfileReadOnlyPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/broadcast"
                element={
                  <ProtectedRoute>
                    <BroadcastPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/withdrawls"
                element={
                  <ProtectedRoute>
                    <WithdrawalsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/withdrawls/:id"
                element={
                  <ProtectedRoute>
                    <WithdrawalDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProcessedWithdrawalPage />
                  </ProtectedRoute>
                }
              />

              {/* Events Routes */}
              <Route
                path="/allevents"
                element={
                  <ProtectedRoute>
                    <AllEventsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/addevent"
                element={
                  <ProtectedRoute>
                    <AddEventPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/allevents/edit/:id"
                element={
                  <ProtectedRoute>
                    <EditEventPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/allevents/:id"
                element={
                  <ProtectedRoute>
                    <EventDetailPage />
                  </ProtectedRoute>
                }
              />

              {/* Users Routes */}
              <Route
                path="/users"
                element={
                  <ProtectedRoute>
                    <UsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user-detail/:userId"
                element={
                  <ProtectedRoute>
                    <UserDetailPage />
                  </ProtectedRoute>
                }
              />

              {/* Course Management Route */}
              <Route
                path="/course-management"
                element={
                  <ProtectedRoute>
                    <CourseManagementPage />
                  </ProtectedRoute>
                }
              />

              {/* Blogs Route */}
              <Route
                path="/blogs"
                element={
                  <ProtectedRoute>
                    <BlogsPage />
                  </ProtectedRoute>
                }
              />

              {/* College Fees Route */}
              <Route
                path="/college-fees"
                element={
                  <ProtectedRoute>
                    <CollegeFeesPage />
                  </ProtectedRoute>
                }
              />

              {/* PCSAT Registrations Route */}
              <Route
                path="/pcsat-registrations"
                element={
                  <ProtectedRoute>
                    <PCSATRegistrationsPage />
                  </ProtectedRoute>
                }
              />

              {/* MHT-CET Option Form Route */}
              <Route
                path="/option-form-registrations"
                element={
                  <ProtectedRoute>
                    <OptionFormRegistrationsPage />
                  </ProtectedRoute>
                }
              />

              {/* Analytics Route - Super Admin only */}
              <Route
                path="/analytics"
                element={
                  <ProtectedRoute>
                    <AnalyticsPage />
                  </ProtectedRoute>
                }
              />

              {/* Test Series Route */}
              <Route
                path="/test-series"
                element={
                  <ProtectedRoute>
                    <TestSeriesPage />
                  </ProtectedRoute>
                }
              />

              {/* Grand Mock Test Route */}
              <Route
                path="/grand-mock-tests"
                element={
                  <ProtectedRoute>
                    <GrandMockTestPage />
                  </ProtectedRoute>
                }
              />

              {/* Enquiries Route - Super Admin only */}
              <Route
                path="/enquiries"
                element={
                  <ProtectedRoute>
                    <EnquiriesPage />
                  </ProtectedRoute>
                }
              />

              {/* Referrers Route - Super Admin only */}
              <Route
                path="/referrers"
                element={
                  <ProtectedRoute>
                    <ReferrersPage />
                  </ProtectedRoute>
                }
              />

              {/* Leads CRM Route - Super Admin only */}
              <Route
                path="/leads"
                element={
                  <ProtectedRoute>
                    <LeadsPage />
                  </ProtectedRoute>
                }
              />

              {/* NEET college lookup - super admin + callers */}
              <Route
                path="/neet-predictor"
                element={
                  <ProtectedRoute>
                    <NeetPredictorPage />
                  </ProtectedRoute>
                }
              />

              {/* My Leads Route - Caller role */}
              <Route
                path="/my-leads"
                element={
                  <ProtectedRoute>
                    <MyLeadsPage />
                  </ProtectedRoute>
                }
              />

              {/* Study Abroad Leads Route - Caller + Super Admin */}
              <Route
                path="/abroad-leads"
                element={
                  <ProtectedRoute>
                    <AbroadLeadsPage />
                  </ProtectedRoute>
                }
              />

              {/* Admin Appointments Route - Super Admin only */}
              <Route
                path="/admin-appointments"
                element={
                  <ProtectedRoute>
                    <AdminAppointmentsPage />
                  </ProtectedRoute>
                }
              />

              {/* Create Admin Route - Super Admin only */}
              <Route
                path="/create-admin"
                element={
                  <ProtectedRoute>
                    <CreateAdminPage />
                  </ProtectedRoute>
                }
              />

              {/* ProBuddies Routes */}
              <Route
                path="/probuddies"
                element={
                  <ProtectedRoute>
                    <ProBuddiesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/probuddy-profile"
                element={
                  <ProtectedRoute>
                    <ProBuddyProfilePage />
                  </ProtectedRoute>
                }
              />

                {/* Error Pages */}
                <Route path="/error" element={<ErrorPage />} />
                <Route path="/not-found" element={<NotFoundPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </MainLayout>
        </AuthInitializer>
      </BrowserRouter>
      </div>
    </ThemeProvider>
  )
}

export default App
